import { gql, useMutation, useQuery } from '@apollo/client';
import { useAuth } from '../contexts/AuthContext';
import {
  AddGuestSpaceFavoriteMutation,
  AddGuestSpaceFavoriteMutationVariables,
  GuestFavoriteSpacesQuery,
  RemoveGuestSpaceFavoriteMutation,
  RemoveGuestSpaceFavoriteMutationVariables,
} from '@pv/common/graphql/operations';

const MARKETPLACE_SPACE_FRAGMENT = gql`
  fragment MarketplaceSpaceFields on MarketplaceSpace {
    id
    name
    description
    displayInDirectory
    seated
    standing
    slug
    pathname
    images(limit: 1) {
      id
      url
      filename
    }
    venue {
      id
      name
      slug
      type
      liveAvailability
      priceCategory
      address {
        id
        streetAddress1
        streetAddress2
        city
        state
        latitude
        longitude
      }
    }
    expressBookEnabled
  }
`;

const GUEST_FAVORITE_SPACES_QUERY = gql`
  ${MARKETPLACE_SPACE_FRAGMENT}
  query GuestFavoriteSpaces {
    authenticatedGuest {
      id
      favoriteSpaces {
        ...MarketplaceSpaceFields
      }
    }
  }
`;

const ADD_GUEST_SPACE_FAVORITE_MUTATION = gql`
  ${MARKETPLACE_SPACE_FRAGMENT}
  mutation AddGuestSpaceFavorite($input: AddGuestSpaceFavoriteInput!) {
    addGuestSpaceFavorite(input: $input) {
      space {
        ...MarketplaceSpaceFields
      }
      errors {
        message
      }
    }
  }
`;

const REMOVE_GUEST_SPACE_FAVORITE_MUTATION = gql`
  mutation RemoveGuestSpaceFavorite($input: RemoveGuestSpaceFavoriteInput!) {
    removeGuestSpaceFavorite(input: $input) {
      space {
        id
        slug
      }
      errors {
        message
      }
    }
  }
`;

export const useGuestSpaceFavorites = () => {
  const { guest } = useAuth();

  const { data, loading, refetch } = useQuery<GuestFavoriteSpacesQuery>(
    GUEST_FAVORITE_SPACES_QUERY,
    {
      skip: !guest,
    },
  );

  const [addFavorite] = useMutation<
    AddGuestSpaceFavoriteMutation,
    AddGuestSpaceFavoriteMutationVariables
  >(ADD_GUEST_SPACE_FAVORITE_MUTATION);

  const [removeFavorite] = useMutation<
    RemoveGuestSpaceFavoriteMutation,
    RemoveGuestSpaceFavoriteMutationVariables
  >(REMOVE_GUEST_SPACE_FAVORITE_MUTATION);

  const toggleFavorite = async (spaceSlug: string, isFavorited: boolean) => {
    try {
      if (isFavorited) {
        await removeFavorite({
          variables: { input: { spaceSlug } },
          update: (cache, { data }) => {
            if (data?.removeGuestSpaceFavorite?.space) {
              const spaceSlug = data.removeGuestSpaceFavorite.space.slug;

              const existingData = cache.readQuery({
                query: GUEST_FAVORITE_SPACES_QUERY,
              }) as any;

              if (existingData?.authenticatedGuest?.favoriteSpaces) {
                const updatedFavorites =
                  existingData.authenticatedGuest.favoriteSpaces.filter(
                    (space: any) => space.slug !== spaceSlug,
                  );

                cache.writeQuery({
                  query: GUEST_FAVORITE_SPACES_QUERY,
                  data: {
                    authenticatedGuest: {
                      ...existingData.authenticatedGuest,
                      favoriteSpaces: updatedFavorites,
                    },
                  },
                });
              }
            }
          },
        });
      } else {
        await addFavorite({
          variables: { input: { spaceSlug } },
          update: (cache, { data }) => {
            if (data?.addGuestSpaceFavorite?.space) {
              const existingData = cache.readQuery({
                query: GUEST_FAVORITE_SPACES_QUERY,
              }) as any;

              if (existingData?.authenticatedGuest?.favoriteSpaces) {
                const updatedFavorites = [
                  ...existingData.authenticatedGuest.favoriteSpaces,
                  data.addGuestSpaceFavorite.space,
                ];

                cache.writeQuery({
                  query: GUEST_FAVORITE_SPACES_QUERY,
                  data: {
                    authenticatedGuest: {
                      ...existingData.authenticatedGuest,
                      favoriteSpaces: updatedFavorites,
                    },
                  },
                });
              }
            }
          },
        });
      }
      return true;
    } catch (error) {
      console.error('Error toggling favorite:', error);
      return false;
    }
  };

  return {
    favoriteSpaces: data?.authenticatedGuest?.favoriteSpaces || [],
    loading,
    refetch,
    toggleFavorite,
  };
};
